import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InstantSearch, SearchBox, useSearchBox, useRefinementList } from "react-instantsearch-hooks-web";
import { createSafeSearchClient } from "../utils/safeSearchClient";
import IDX from "../utils/indices";
import BrandFilter from "../components/BrandFilter";
import { db, auth } from '../lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useUser } from '../context/UserProvider';
import { useTranslation } from 'react-i18next';

function HomeSearchControls({ onGo, t }) {
  const { query } = useSearchBox();
  const { items } = useRefinementList({
    attribute: "data.brand",
    operator: "or",
    limit: 50,
    sortBy: ["name:asc"],
  });

  const selectedBrands = React.useMemo(
    () => items.filter((i) => i.isRefined).map((i) => i.label),
    [items]
  );

  const handleSubmit = React.useCallback(
    (e) => {
      if (e && typeof e.preventDefault === "function") e.preventDefault();
      const params = new URLSearchParams();
      if (query && query.trim()) params.set("q", query.trim());
      for (const b of selectedBrands) params.append("brand", b);
      onGo?.(params);
    },
    [query, selectedBrands, onGo]
  );

  return (
    <div className="mb-3 sm:mb-4">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 sm:gap-3">
        <div className="md:col-span-8">
          <SearchBox
            placeholder={t('home.searchPlaceholder')}
            onSubmit={handleSubmit}
            classNames={{
              root: "w-full",
              form: "w-full rounded-2xl ring-1 ring-white/10 bg-emerald-900/60",
              input: "bg-transparent text-emerald-50 placeholder:text-emerald-300",
              submit: "text-emerald-300",
              reset: "text-emerald-300",
            }}
          />
        </div>
        <div className="md:col-span-4">
          <BrandFilter />
        </div>
      </div>
      <div className="mt-2 flex md:hidden">
        <button
          type="button"
          onClick={handleSubmit}
          className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 px-4 py-2 text-sm font-medium text-white"
        >
          {t('home.searchButton')}
        </button>
      </div>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { ready, uid } = useUser();
  const { t } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const appId = process.env.REACT_APP_ALGOLIA_APP_ID;
  const apiKey = process.env.REACT_APP_ALGOLIA_SEARCH_KEY;
  const searchClient = React.useMemo(() => createSafeSearchClient(appId, apiKey), [appId, apiKey]);
  const defaultIndex = React.useMemo(() => IDX.converters, []);

  useEffect(() => {
    // Only fetch articles if user authentication is ready
    if (!ready) return;

    const fetchArticles = async () => {
      try {
        if (!uid) {
          // User is not authenticated, no articles to fetch
          console.log('User not authenticated, skipping articles fetch');
          setArticles([]);
          return;
        }

        // Ensure we have a fresh token before making Firestore request
        const user = auth.currentUser;
        if (user) {
          await user.getIdToken(true); // Force refresh
        }

        const articlesCollection = collection(db, 'articles');
        const articlesSnapshot = await getDocs(articlesCollection);

        const articlesData = articlesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort by date descending (most recent first)
        articlesData.sort((a, b) => new Date(b.date) - new Date(a.date));

        setArticles(articlesData);
      } catch (error) {
        console.error('Error fetching articles:', error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [ready, uid]);

  const handleArticleClick = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Show loading while auth is initializing or articles are loading
  if (!ready || loading) {
    return (
      <div className="min-h-[calc(100vh-3.5rem)] p-6 text-white">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p>{!ready ? t('common.initializing') : t('home.loadingArticles')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-3.5rem)] p-0 sm:p-0 text-white">
      <InstantSearch searchClient={searchClient} indexName={defaultIndex}>
        <HomeSearchControls onGo={(params) => navigate(`/converters?${params.toString()}`)} t={t} />
      </InstantSearch>

      <div className="w-full px-6">
        {articles.length === 0 ? (
          <div className="text-gray-400">
            <p>{t('home.noArticlesFound')}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-6">
            {articles.map((article) => (
              <div
                key={article.id}
                className="w-full max-w-md bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                onClick={() => handleArticleClick(article.url)}
              >
                {article.imgURL && (
                  <div className="w-full">
                    <img
                      src={article.imgURL}
                      alt={article.title || 'Article image'}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.src = '/placeholder-image.png'; // fallback image
                      }}
                    />
                  </div>
                )}

                {article.description && (
                  <div className="p-4">
                    <p className="text-gray-600 text-sm">
                      {article.description}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
